import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import { IEvent } from "../../../interfaces/models/IEvent";
import chevronRightButton from "../../../assets/Fonts/chevron-right-button.svg";
import { capitalizeHeader } from "../../../utils/capitalize";
import LocationIcon from "../../../assets/images&icons/Icon-Location.png";
import CalendarIcon from "../../../assets/images&icons/calendar.png";
import UserIcon from "../../../assets/images&icons/location.png";
import WorldServicesGroupIcon from "../../../components/Atomos/Icons/WorldServicesGroupIcon";
import {
  formatEventLocation,
  getVenueDetails,
  formatVenueAddress,
} from "../../../utils/eventLocationFormatter";
import { formatEventDate } from "../../../utils/eventDateFormatter";
import StaffRegistrationEditMode from "./StaffRegistrationEditMode";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../../store/AuthContext";
import {
  getRegistrationByPersonAndEvent,
  checkPrepaidStatus,
} from "../../../Services/Registration";
import { IRegistration } from "../../../interfaces/models/IRegistration";
import { getPaymentByRegistrationId } from "../../../Services/Payment";
import { IPayment } from "../../../interfaces/models/IPayment";
import Success from "../../../components/Atomos/Icons/Success";
import { useParams } from "react-router-dom";
import { useEventLoader } from "../../../hooks/Events/useEventLoader";
import { useOptionalEventDetailData } from "../../../hooks/Events/EventDetailDataContext";

const getGuestFee = (event: IEvent): number | null => {
  const fee = event.fee;
  if (!fee) {
    return null;
  }

  const nowUTC = Date.now();
  const noRefundDateMs = fee.noRefundDate ? fee.noRefundDate * 1000 : null;
  const shouldUseLateFees = noRefundDateMs !== null && nowUTC > noRefundDateMs;

  if (
    shouldUseLateFees &&
    fee.guestLateFee !== null &&
    fee.guestLateFee !== undefined
  ) {
    return fee.guestLateFee;
  }

  return fee.guestFee ?? null;
};

export default function StaffRegistrationTab({
  isEditMode,
  onEditClick,
  onEventUpdate,
  viewMode = "staff",
}: {
  isEditMode: boolean;
  onEditClick: () => void;
  onEventUpdate?: (updatedEvent: IEvent) => void;
  viewMode?: "staff" | "member";
}) {
  const { id } = useParams<{ id: string }>();
  const event = useEventLoader(id);
  const eventDetailData = useOptionalEventDetailData();
  const navigate = useNavigate();
  const { loggedPerson } = useContext(AuthContext);
  const [registration, setRegistration] = useState<IRegistration | null>(null);
  const [isPrepaid, setIsPrepaid] = useState<boolean | null>(null);
  const [existingPayment, setExistingPayment] = useState<IPayment | null>(null);

  useEffect(() => {
    if (viewMode === "member" && event?.id && loggedPerson?.id) {
      const loadRegistration = eventDetailData
        ? eventDetailData.ensureRegistrationLoaded(loggedPerson.id)
        : getRegistrationByPersonAndEvent(loggedPerson.id, event.id);

      loadRegistration
        .then((registrationData) => {
          setRegistration(registrationData);

          // Get payment if registration exists
          if (registrationData?.id && event.id) {
            getPaymentByRegistrationId(registrationData.id)
              .then((payment) => {
                if (payment) {
                  const isValidPayment =
                    payment.eventId === event.id &&
                    payment.person?.some((p) => p.personId === loggedPerson.id);
                  if (isValidPayment) {
                    setExistingPayment(payment);
                  } else {
                    setExistingPayment(null);
                  }
                } else {
                  setExistingPayment(null);
                }
              })
              .catch((error) => {
                setExistingPayment(null);
              });
          } else {
            setExistingPayment(null);
          }

          // Check prepaid status after registration is loaded
          if (event.id && loggedPerson.id) {
            checkPrepaidStatus(event.id, loggedPerson.id, registrationData?.id)
              .then((prepaidStatus) => {
                setIsPrepaid(prepaidStatus);
              })
              .catch((error) => {
                setIsPrepaid(null);
              });
          }
        })
        .catch((error) => {
          setRegistration(null);
          setIsPrepaid(null);
          setExistingPayment(null);
        });
    }
  }, [viewMode, event?.id, loggedPerson?.id, eventDetailData]);
  useEffect(() => {
    if (sessionStorage.getItem("payaPaymentSuccess") && viewMode === "member" && registration) {
      sessionStorage.removeItem("payaPaymentSuccess");
      requestAnimationFrame(() => {
        const thankYouEl = document.getElementById("registration-thank-you");
        if (thankYouEl) {
          thankYouEl.scrollIntoView({ block: "center", behavior: "auto" });
        }
      });
    }
  }, [viewMode, registration]);

  const isRegistered = registration !== null;

  if (!event) {
    return <div>Event not found</div>;
  }
  if (isEditMode) {
    return (
      <div>
        <StaffRegistrationEditMode
          event={event}
          onEventUpdate={onEventUpdate || (() => {})}
          onEditClick={onEditClick}
        />
      </div>
    );
  }

  const getLocationDisplay = () => {
    return formatEventLocation(event);
  };

  const tabs = [
    {
      label: "Where",
      content: (
        <div className="space-y-2 md:space-y-3">
          <div className="flex items-center gap-2 md:gap-3">
            <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
            <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
              {getLocationDisplay()}
            </span>
          </div>

          {event.eventType === "hybrid" && (
            <div className="flex items-center gap-2 md:gap-3">
              <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
              <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                Virtual
              </span>
            </div>
          )}

          {(() => {
            const venueDetails = getVenueDetails(event);
            if (venueDetails && venueDetails.location) {
              return (
                <div className="flex items-center gap-2 md:gap-3">
                  <img
                    src={LocationIcon}
                    alt="Venue location"
                    className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0 -ml-0.5 object-contain"
                  />
                  <div className="flex flex-col">
                    {venueDetails.venueLink ? (
                      <a
                        href={venueDetails.venueLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words hover:underline"
                      >
                        {venueDetails.location}
                      </a>
                    ) : (
                      <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                        {venueDetails.location}
                      </span>
                    )}
                    {formatVenueAddress(venueDetails) && (
                      <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                        {formatVenueAddress(venueDetails)}
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            if (
              event.eventType?.toLowerCase() !== "virtual" &&
              event.location
            ) {
              return (
                <div className="flex items-center gap-2 md:gap-3">
                  <img
                    src={LocationIcon}
                    alt="Event type"
                    className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0 -ml-0.5 object-contain"
                  />
                  {event.venueLink ? (
                    <a
                      href={event.venueLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words"
                    >
                      {capitalizeHeader(event.location || "")}
                    </a>
                  ) : (
                    <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                      {capitalizeHeader(event.location || "")}
                    </span>
                  )}
                </div>
              );
            }
            return null;
          })()}
        </div>
      ),
    },
    {
      label: "When",
      content: (
        <div className="space-y-2 md:space-y-3">
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src={CalendarIcon}
              alt="Event type"
              className="w-4 h-6 md:w-6 md:h-8 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0"
            />
            <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
              {formatEventDate(event)}
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src={UserIcon}
              alt="Event type"
              className="w-4 h-6 md:w-6 md:h-8 lg:w-8 lg:h-10 text-[#C2C2C2] flex-shrink-0"
            />
            <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
              {capitalizeHeader(event.eventType)}
            </span>
          </div>
        </div>
      ),
    },
    {
      label: "Fees",
      content: (
        <div className="flex flex-col">
          {event.fee?.memberFee != null && (
            <div className="mb-4">
              <div className="flex flex-col">
                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  Registration Fee: ${event.fee.memberFee.toFixed(2)}{" "}
                  {event.fee?.currency || "USD"}
                </span>
              </div>
            </div>
          )}
          {event.fee?.lateFee != null && (
            <div className="mb-4">
              <div className="flex flex-col">
                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  Late Registration Fee: ${event.fee.lateFee.toFixed(2)}{" "}
                  {event.fee?.currency || "USD"}
                </span>
                {event.fee?.partialRefundEndDate && (
                  <span className="text-gray-font font-sans text-xs sm:text-xs md:text-sm lg:text-sm font-light italic leading-4 md:leading-5 lg:leading-6 break-words">
                    (late fee after{" "}
                    {(() => {
                      const date = new Date(
                        event.fee.partialRefundEndDate * 1000
                      );
                      const day = date.getUTCDate();
                      const month = date.toLocaleDateString("en-US", {
                        month: "long",
                        timeZone: "UTC",
                      });
                      const year = date.getUTCFullYear();
                      return `${day} ${month} ${year}`;
                    })()}
                    )
                  </span>
                )}
              </div>
            </div>
          )}
          {event.fee?.guestFee != null && (
            <div className="mb-4">
              <div className="flex flex-col">
                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  Guest Fee: ${event.fee.guestFee.toFixed(2)}{" "}
                  {event.fee?.currency || "USD"}
                </span>
              </div>
            </div>
          )}
          {event.fee?.guestLateFee != null && (
            <div className="mb-4">
              <div className="flex flex-col">
                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  Late Guest Fee: ${event.fee.guestLateFee.toFixed(2)}{" "}
                  {event.fee?.currency || "USD"}
                </span>
              </div>
            </div>
          )}
          {registration && (registration as any).additionalFee != null && (registration as any).additionalFee > 0 && (
            <div className="mb-4">
              <div className="flex flex-col">
                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  Additional Fee: ${((registration as any).additionalFee as number).toFixed(2)}{" "}
                  {event.fee?.currency || "USD"}
                </span>
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];
  return (
    <div className="flex flex-col justify-center items-center gap-6">
      <p className="text-primary font-decimal text-[32px] font-medium leading-[36px] text-left w-full">
        Delegate Registration
      </p>
      {viewMode === "member" && isRegistered && (
        <div id="registration-thank-you" className="flex flex-col items-center w-full gap-6 bg-gray-15 p-8">
          <Success className="w-12 h-12 text-primary" />
          <p className="text-primary font-sans text-base lg:text-lg font-normal leading-6 text-center">
            Thank you for registering to {event.title}. An email will be sent
            with payment information.
          </p>
          <div className="flex gap-6 w-[45rem] justify-center">
            {(() => {
              const hasPendingPayment =
                registration?.paymentStatus === "pending" && !isPrepaid;
              const guestFee = getGuestFee(event);
              const hasGuest =
                registration?.guestName && registration.guestName.trim() !== "";
              const hasUnpaidGuest =
                hasGuest &&
                guestFee != null &&
                (!existingPayment || !existingPayment.guestPaid);
              const shouldShowButton = hasPendingPayment || hasUnpaidGuest;

              return shouldShowButton ? (
                <LegacyButton
                  text="PAY ONLINE BY CREDIT CARD"
                  color="primary"
                  content="textIcon"
                  icon={
                    <img
                      src={chevronRightButton}
                      alt="chevron"
                      className="w-3 h-3"
                    />
                  }
                  onClick={() => {
                    const contentDiv =
                      document.getElementById("content-container");
                    if (contentDiv && contentDiv.scrollTop > 0) {
                      sessionStorage.setItem(
                        "eventScrollPosition",
                        contentDiv.scrollTop.toString()
                      );
                    }
                    navigate(`/events/${event.id}/registration/payment`);
                  }}
                />
              ) : null;
            })()}
            <LegacyButton
              text="MODIFY REGISTRATION"
              color="primary"
              content="textIcon"
              icon={
                <img
                  src={chevronRightButton}
                  alt="chevron"
                  className="w-3 h-3"
                />
              }
              onClick={() => navigate(`/events/${event.id}/registration/form`)}
            />
          </div>
        </div>
      )}
      <div
        className={`flex flex-col items-start h-auto md:h-[229px] pt-2 md:pt-4 pb-3 md:pb-6 px-0 bg-gray-15 transition-all text-left flex-1 min-w-full md:min-w-0 overflow-hidden w-full`}
      >
        <div className="border-b border-primary pb-0 flex-shrink-0 mb-4">
          <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans pl-4 md:pl-8">
            {event.title}
          </h3>
        </div>
        <div className="block text-left px-4 md:px-8 flex-1 w-full overflow-hidden text-gray-font font.roboto text-base lg:text-[18px] font-normal leading-4 md:leading-5 lg:leading-6 break-words">
          {event.description || ""}
        </div>
        <div className="flex flex-col md:flex-row flex-wrap gap-2 mt-2 w-full">
          {tabs.slice(0, 3).map((tab, index) => {
            const isFeesTab = tab.label === "Fees";
            return (
              <div
                key={index}
                className={`flex flex-col items-start ${
                  isFeesTab ? "h-auto" : "h-auto md:h-[229px]"
                } pt-2 md:pt-4 pb-3 md:pb-6 px-0 bg-gray-15 transition-all text-left flex-1 min-w-full md:min-w-0 ${
                  isFeesTab ? "overflow-visible" : "overflow-hidden"
                }`}
              >
                <div className="border-b border-primary pb-0 flex-shrink-0 mb-4">
                  <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans pl-4 md:pl-8">
                    {tab.label}
                  </h3>
                </div>
                <div
                  className={`block text-left px-4 md:px-8 flex-1 w-full ${
                    isFeesTab ? "overflow-visible" : "overflow-hidden"
                  }`}
                >
                  {tab.content}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {viewMode === "member" && !isRegistered && (
        <>
          <LegacyButton
            text="REGISTER"
            className="w-full md:w-[205px] mt-4 mx-auto"
            color="primary"
            content="textIcon"
            icon={
              <img src={chevronRightButton} alt="chevron" className="w-3 h-3" />
            }
            onClick={() => navigate(`/events/${event.id}/registration/form`)}
          />
        </>
      )}
      {event.fee?.cancellationPolicy && (
        <div className="bg-primary text-white font-Roboto text-base lg:text-[18px] font-[300] leading-4 md:leading-5 lg:leading-6 break-words p-4 mt-4 w-full">
          <p className="font-semibold mb-2">Refund Policy</p>
          <p>{event.fee?.cancellationPolicy || "No refund policy available"}</p>
        </div>
      )}
    </div>
  );
}
